import { formatearFechaHora } from "@gestiondoc/shared";
import { VistaDeContenido } from "../editor/VistaDeContenido.tsx";
import type { Detalle, RevisionDetalle } from "../tipos.ts";
import { Aviso, Tarjeta } from "./ui.tsx";

/**
 * Visor embebido de una revisión: el PDF sellado si ya existe, si no la vista previa (el mismo PDF
 * subido, o la conversión de LibreOffice si es Word o Excel; la hoja redactada si no hay PDF). Lo
 * que ven quien revisa y quien aprueba es lo que están firmando.
 */
export function Visor({
  revision,
  fallo,
  informativo,
}: {
  revision: RevisionDetalle;
  fallo: Detalle["trabajoFallido"];
  informativo: boolean;
}) {
  const pdf = revision.pdfSellado ?? revision.pdfVista;
  // Una revisión redactada se ve como la hoja que se firma; el PDF sellado llega al aprobar.
  if (revision.redactada && !pdf) {
    return (
      <Tarjeta
        titulo="Vista previa"
        ayuda={
          informativo
            ? "Así se publicó. El PDF se genera al publicar."
            : "Así se ve lo que se firma. El PDF con el sello se genera al aprobar."
        }
        className="mt-5"
      >
        <VistaDeContenido revisionId={revision.id} />
      </Tarjeta>
    );
  }
  return (
    <Tarjeta
      titulo="Vista previa"
      ayuda={
        revision.pdfSellado
          ? "PDF sellado: las páginas del documento con el pie de control y la hoja de firmas al final."
          : revision.origen === "importado"
            ? "El archivo tal como se aprobó en papel: no lleva sello de GestionDOC."
            : "Así se ve el archivo que se firma. El sello se aplica al aprobar."
      }
      className="mt-5"
    >
      {!pdf && fallo ? (
        <TrabajoFallido fallo={fallo} />
      ) : pdf ? (
        <iframe
          key={pdf.id}
          src={`/archivos/${pdf.id}?ver=1`}
          title={`Vista previa de ${pdf.nombre}`}
          className="h-[760px] w-full rounded border border-borde bg-fondo"
        />
      ) : (
        <div className="grid h-[200px] place-items-center rounded border border-dashed border-borde text-[12.5px] text-texto-3">
          Preparando la vista previa: el archivo se está convirtiendo a PDF…
        </div>
      )}
    </Tarjeta>
  );
}

/** El worker agotó los reintentos: se dice, en vez de mostrar "generando…" para siempre. */
export function TrabajoFallido({ fallo }: { fallo: NonNullable<Detalle["trabajoFallido"]> }) {
  return (
    <Aviso>
      No se pudo generar {fallo.que === "sello" ? "el PDF sellado" : "la vista previa"} (
      {formatearFechaHora(fallo.cuando)}): {fallo.motivo}. Avisa a Soporte Tecnológico.
    </Aviso>
  );
}
